/**
 * useNotificationPrefs.js — shared notification-preferences state for the
 * portal, backing both the Settings toggles and the Notifications page's
 * category filter.
 *
 * Not colocated with either page: both need the exact same server state
 * (UserV1.notificationPrefs, the same field the mobile app and the backend's
 * push gating read), so a single hook instance per mount avoids two
 * independent fetches disagreeing while a toggle is in flight. Each call site
 * gets its own instance/fetch (there is no shared store), which is fine here
 * — Settings and Notifications are never both mounted at once in this
 * single-page-at-a-time router.
 */
import { useState, useEffect, useCallback } from "react";
import {
  getNotificationPrefs,
  updateNotificationPrefs,
  DEFAULT_NOTIFICATION_PREFS,
} from "../services/notificationPrefs.api";

export function useNotificationPrefs() {
  const [prefs, setPrefs] = useState(DEFAULT_NOTIFICATION_PREFS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const server = await getNotificationPrefs();
        if (!cancelled) setPrefs({ ...DEFAULT_NOTIFICATION_PREFS, ...server });
      } catch {
        // Leave the defaults in place — the toggles still work locally and
        // the next successful fetch (e.g. a page revisit) reconciles them.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Optimistic: paint immediately, persist in the background, revert on
  // failure so a rejected write can't leave the toggle showing a lie.
  const setPref = useCallback((key, value) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
    updateNotificationPrefs({ [key]: value }).catch(() => {
      setPrefs((prev) => ({ ...prev, [key]: !value }));
    });
  }, []);

  return { prefs, loading, setPref };
}
